import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  approveVersionThunk,
  clearCurrent,
  createVersionThunk,
  loadDocument,
  obsoleteVersionThunk,
  putInForceThunk,
  reviewVersionThunk,
  submitForReviewThunk,
} from '../features/documents/documentsSlice';
import type {
  AcknowledgementAudience,
  DocumentStatus,
  DocumentVersion,
  ReadingReport,
  VersionPayload,
} from '../features/documents/types';
import {
  acknowledgeVersion,
  downloadReadingReportCsv,
  fetchMyAcknowledgement,
  fetchReadingReport,
} from '../features/documents/documentsApi';
import { PutInForceAudienceModal } from '../features/documents/PutInForceAudienceModal';
import { ExportPdfButton } from '../features/reports/ExportPdfButton';
import { AppDispatch, RootState } from '../store/store';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { DateCell } from '../components/DateCell';
import { AttachmentsList } from '../components/AttachmentsList';
import { showToast } from '../components/ui/toast.utils';
import { loadRoles } from '../features/roles/rolesSlice';

const STATUS_LABEL: Record<DocumentStatus, string> = {
  draft: 'Borrador',
  in_review: 'En revisión',
  approved: 'Aprobado',
  in_force: 'Vigente',
  obsolete: 'Obsoleto',
};

const STATUS_CLASS: Record<DocumentStatus, string> = {
  draft: 'bg-zinc-200 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200',
  in_review: 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200',
  approved: 'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-200',
  in_force: 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-200',
  obsolete: 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-200',
};

const versionSchema = z.object({
  version: z.string().min(1, 'Requerido').max(32),
  changeSummary: z.string().trim().max(5000).optional(),
});
type VersionForm = z.infer<typeof versionSchema>;

export function DocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const { canEdit } = usePlatformAccessLevel('documents.access');
  const { current, versions } = useSelector((s: RootState) => s.documents);
  const [newVersionOpen, setNewVersionOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    dispatch(loadDocument(id));
    return () => {
      dispatch(clearCurrent());
    };
  }, [dispatch, id]);

  const reload = useCallback(() => {
    if (id) dispatch(loadDocument(id));
  }, [dispatch, id]);

  if (!current) {
    return <p className="text-muted-foreground">Cargando…</p>;
  }

  return (
    <div className="space-y-4">
      <div>
        <Link to="/documentos" className="text-sm text-primary hover:underline">
          ← Volver a documentos
        </Link>
      </div>
      <header className="border rounded-md p-4 bg-card">
        <div className="flex justify-between items-start gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold">{current.title}</h1>
            <p className="text-sm text-muted-foreground font-mono">{current.code}</p>
          </div>
          <div className="text-sm space-y-1">
            <div>
              <span className="text-muted-foreground">Tipo: </span>
              {current.type?.name ?? '—'}
            </div>
            <div>
              <span className="text-muted-foreground">Estado: </span>
              <span className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_CLASS[current.status]}`}>
                {STATUS_LABEL[current.status]}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Versión vigente: </span>
              <span className="font-mono">{current.currentVersion?.version ?? '—'}</span>
            </div>
            {current.ownerArea && (
              <div>
                <span className="text-muted-foreground">Área: </span>
                {current.ownerArea}
              </div>
            )}
          </div>
        </div>
        {current.notes && (
          <p className="mt-3 text-sm text-muted-foreground border-t pt-3">{current.notes}</p>
        )}
      </header>

      <section className="space-y-3">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold">Versiones</h2>
          {canEdit && (
            <button
              onClick={() => setNewVersionOpen(!newVersionOpen)}
              className="rounded-md px-3 py-1.5 text-sm bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {newVersionOpen ? 'Cancelar' : '+ Nueva versión'}
            </button>
          )}
        </div>

        {newVersionOpen && id && (
          <NewVersionForm
            documentId={id}
            onCancel={() => setNewVersionOpen(false)}
            onCreated={() => {
              setNewVersionOpen(false);
              reload();
            }}
          />
        )}

        <VersionsTable
          versions={versions}
          canEdit={canEdit}
          isCurrentVersionId={current.currentVersionId ?? null}
          onChanged={reload}
        />
      </section>

      {current.currentVersion?.status === 'in_force' && current.currentVersionId && (
        <ReadingComplianceSection versionId={current.currentVersionId} />
      )}
    </div>
  );
}

function ReadingComplianceSection({ versionId }: { versionId: string }) {
  const [report, setReport] = useState<ReadingReport | null>(null);
  const [hasAcknowledged, setHasAcknowledged] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [reportData, myAck] = await Promise.all([
        fetchReadingReport(versionId),
        fetchMyAcknowledgement(versionId),
      ]);
      setReport(reportData);
      setHasAcknowledged(Boolean(myAck));
    } catch (e) {
      setError((e as Error).message ?? 'No se pudo cargar el cumplimiento de lectura');
    } finally {
      setLoading(false);
    }
  }, [versionId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleAcknowledge = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await acknowledgeVersion(versionId);
      await reload();
    } catch (e) {
      setError((e as Error).message ?? 'No se pudo registrar el acuse');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-muted-foreground">Cargando cumplimiento de lectura…</p>;
  }

  return (
    <section className="border rounded-md p-4 space-y-3 bg-card">
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div>
          <h2 className="text-lg font-semibold">Cumplimiento de lectura</h2>
          <p className="text-sm text-muted-foreground">
            Acuse &quot;leído y entendido&quot; de la versión vigente (firma simple con evidencia de
            identidad y fecha).
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <ExportPdfButton
            reportType="document_reading"
            resourceId={versionId}
            permissionKey="documents.access"
            label="Exportar PDF"
            className="rounded-md px-3 py-1.5 text-sm border bg-background hover:bg-muted"
          />
          <button
            type="button"
            className="rounded-md px-3 py-1.5 text-sm border bg-background hover:bg-muted disabled:opacity-50"
            disabled={exportingCsv}
            onClick={() => {
              setExportingCsv(true);
              void downloadReadingReportCsv(versionId)
                .catch(() => setError('No se pudo exportar el CSV'))
                .finally(() => setExportingCsv(false));
            }}
          >
            {exportingCsv ? 'Exportando…' : 'Exportar CSV'}
          </button>
          {hasAcknowledged === false && (
            <button
              type="button"
              onClick={handleAcknowledge}
              disabled={submitting}
              className="rounded-md px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {submitting ? 'Registrando…' : 'Confirmar lectura'}
            </button>
          )}
          {hasAcknowledged && (
            <span className="text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-1.5 dark:text-green-200 dark:bg-green-950/40 dark:border-green-800">
              Lectura confirmada
            </span>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {report && (
        <>
          <div className="flex flex-wrap gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">% leído: </span>
              <span className="font-semibold">{report.percentRead}%</span>
              <span className="text-muted-foreground">
                {' '}
                ({report.acknowledgedCount}/{report.totalUsers})
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Audiencia: </span>
              <span>
                {report.audienceMode === 'all_org_users'
                  ? 'Toda la organización'
                  : 'Restringida (roles/grupos/usuarios)'}
              </span>
            </div>
          </div>

          {report.pending.length > 0 && (
            <div>
              <h3 className="text-sm font-medium mb-1">Pendientes de acuse</h3>
              <ul className="text-sm text-muted-foreground list-disc pl-5 max-h-32 overflow-y-auto">
                {report.pending.map((u) => (
                  <li key={u.id}>{u.email}</li>
                ))}
              </ul>
            </div>
          )}

          {report.acknowledged.length > 0 && (
            <div>
              <h3 className="text-sm font-medium mb-1">Usuarios que confirmaron lectura</h3>
              <ul className="text-sm text-muted-foreground list-disc pl-5 max-h-32 overflow-y-auto">
                {report.acknowledged.map((u) => (
                  <li key={u.id}>
                    {u.email}
                    {u.acknowledgedAt && (
                      <span className="text-xs">
                        {' '}
                        — <DateCell value={u.acknowledgedAt} />
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function NewVersionForm({
  documentId,
  onCancel,
  onCreated,
}: {
  documentId: string;
  onCancel: () => void;
  onCreated: () => void;
}) {
  const dispatch = useDispatch<AppDispatch>();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<VersionForm>({
    resolver: zodResolver(versionSchema),
    defaultValues: { version: '', changeSummary: '' },
  });

  const submit = handleSubmit(async (values) => {
    const payload: VersionPayload = {
      version: values.version,
      fileUrl: null,
      fileName: null,
      changeSummary: values.changeSummary && values.changeSummary.length > 0 ? values.changeSummary : null,
    };
    await dispatch(createVersionThunk({ documentId, payload })).unwrap();
    onCreated();
  });

  return (
    <form
      onSubmit={submit}
      className="border border-border rounded-md p-3 grid grid-cols-1 md:grid-cols-2 gap-3 bg-muted/30"
    >
      <Field label="Versión *" error={errors.version?.message}>
        <input
          className="border border-input rounded-md px-2 py-1.5 w-full bg-background"
          placeholder="1.0, 2.1, …"
          {...register('version')}
        />
      </Field>
      <p className="md:col-span-2 text-xs text-muted-foreground">
        Tras crear la versión, subí el PDF u otros archivos en la columna Archivo de la tabla.
      </p>
      <Field label="Resumen de cambios" error={errors.changeSummary?.message} colSpan={2}>
        <textarea
          rows={2}
          className="border border-input rounded-md px-2 py-1.5 w-full bg-background"
          {...register('changeSummary')}
        />
      </Field>
      <div className="md:col-span-2 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md px-3 py-1.5 text-sm bg-secondary text-secondary-foreground hover:bg-secondary/80"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-md px-3 py-1.5 text-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {isSubmitting ? 'Creando…' : 'Crear versión'}
        </button>
      </div>
    </form>
  );
}

function VersionsTable({
  versions,
  canEdit,
  isCurrentVersionId,
  onChanged,
}: {
  versions: DocumentVersion[];
  canEdit: boolean;
  isCurrentVersionId: string | null;
  onChanged: () => void;
}) {
  const dispatch = useDispatch<AppDispatch>();
  const { items: roles } = useSelector((s: RootState) => s.roles);
  const [forceModalVersion, setForceModalVersion] = useState<DocumentVersion | null>(null);

  useEffect(() => {
    dispatch(loadRoles({ pageSize: 200 }));
  }, [dispatch]);

  const roleOptions = roles.map((role) => role.name);

  const handleAction = useCallback(
    async (action: 'submit' | 'review' | 'approve' | 'obsolete', v: DocumentVersion) => {
      try {
        if (action === 'submit') await dispatch(submitForReviewThunk(v.id)).unwrap();
        if (action === 'review') await dispatch(reviewVersionThunk(v.id)).unwrap();
        if (action === 'approve') await dispatch(approveVersionThunk(v.id)).unwrap();
        if (action === 'obsolete') await dispatch(obsoleteVersionThunk(v.id)).unwrap();
        const successMessage =
          action === 'submit'
            ? 'Versión enviada a revisión.'
            : action === 'review'
              ? 'Versión marcada como revisada.'
              : action === 'approve'
                ? 'Versión aprobada.'
                : 'Versión marcada como obsoleta.';
        showToast(successMessage, 'success');
        onChanged();
      } catch (e) {
        showToast((e as Error).message ?? 'Acción no permitida', 'error');
      }
    },
    [dispatch, onChanged],
  );

  const handlePutInForce = async (audience: AcknowledgementAudience | null) => {
    if (!forceModalVersion) return;
    await dispatch(
      putInForceThunk({ versionId: forceModalVersion.id, audience }),
    ).unwrap();
    setForceModalVersion(null);
    onChanged();
  };

  return (
    <>
      <PutInForceAudienceModal
        open={Boolean(forceModalVersion)}
        versionLabel={forceModalVersion?.version ?? ''}
        roleOptions={roleOptions}
        onCancel={() => setForceModalVersion(null)}
        onConfirm={handlePutInForce}
      />
      <div className="border border-border rounded-md overflow-hidden bg-card">
      <table className="w-full text-sm">
        <thead className="bg-muted">
          <tr>
            <Th>Versión</Th>
            <Th>Estado</Th>
            <Th>Archivo</Th>
            <Th>Vigencia</Th>
            <Th>Revisado por</Th>
            <Th>Aprobado por</Th>
            <Th>Acciones</Th>
          </tr>
        </thead>
        <tbody>
          {versions.length === 0 && (
            <tr>
              <td colSpan={7} className="px-3 py-4 text-center text-muted-foreground">
                Sin versiones
              </td>
            </tr>
          )}
          {versions.map((v) => (
            <tr
              key={v.id}
              className={`border-t border-border hover:bg-muted/40 ${
                v.id === isCurrentVersionId
                  ? 'bg-green-50 dark:bg-green-950/25'
                  : ''
              }`}
            >
              <td className="px-3 py-2 font-mono">{v.version}</td>
              <td className="px-3 py-2">
                <span className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_CLASS[v.status]}`}>
                  {STATUS_LABEL[v.status]}
                </span>
                {v.status === 'in_review' && v.reviewedAt && (
                  <span className="ml-1 inline-block rounded px-2 py-0.5 text-xs bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-200">
                    Revisada
                  </span>
                )}
              </td>
              <td className="px-3 py-2">
                <AttachmentsList
                  resourceType="document_version"
                  resourceId={v.id}
                  readOnly={!canEdit}
                  title=""
                  accept=".pdf,.doc,.docx"
                />
              </td>
              <td className="px-3 py-2">
                {v.effectiveAt ? (
                  <>
                    <DateCell value={v.effectiveAt} />
                    {v.expiresAt && (
                      <span className="text-muted-foreground">
                        {' '}
                        → <DateCell value={v.expiresAt} />
                      </span>
                    )}
                  </>
                ) : (
                  '—'
                )}
              </td>
              <td className="px-3 py-2 text-xs">
                {v.reviewedBy?.email ? (
                  <>
                    {v.reviewedBy.email}
                    {v.reviewedAt && (
                      <span className="block text-muted-foreground">
                        <DateCell value={v.reviewedAt} />
                      </span>
                    )}
                  </>
                ) : (
                  '—'
                )}
              </td>
              <td className="px-3 py-2 text-xs">{v.approvedBy?.email ?? '—'}</td>
              <td className="px-3 py-2">
                {canEdit && (
                  <div className="flex flex-wrap gap-1">
                    {v.status === 'draft' && (
                      <ActionBtn onClick={() => void handleAction('submit', v)} label="A revisión" />
                    )}
                    {v.status === 'in_review' && (
                      <>
                        {!v.reviewedAt && (
                          <ActionBtn onClick={() => void handleAction('review', v)} label="Marcar revisado" />
                        )}
                        <ActionBtn
                          onClick={() => void handleAction('approve', v)}
                          label="Aprobar"
                          variant="primary"
                        />
                      </>
                    )}
                    {v.status === 'approved' && (
                      <ActionBtn
                        onClick={() => setForceModalVersion(v)}
                        label="Poner en vigencia"
                        variant="primary"
                      />
                    )}
                    {v.status !== 'obsolete' && v.status !== 'in_force' && (
                      <ActionBtn
                        onClick={() => void handleAction('obsolete', v)}
                        label="Obsoleto"
                        variant="danger"
                      />
                    )}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </>
  );
}

function ActionBtn({
  onClick,
  label,
  variant,
}: {
  onClick: () => void;
  label: string;
  variant?: 'primary' | 'danger';
}) {
  const cls =
    variant === 'primary'
      ? 'bg-primary text-primary-foreground hover:bg-primary/90'
      : variant === 'danger'
        ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
        : 'bg-secondary text-secondary-foreground hover:bg-secondary/80';
  return (
    <button type="button" onClick={onClick} className={`text-xs rounded px-2 py-1 ${cls}`}>
      {label}
    </button>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="text-left px-3 py-2 font-medium">{children}</th>;
}

function Field({
  label,
  children,
  error,
  colSpan,
}: {
  label: string;
  children: React.ReactNode;
  error?: string;
  colSpan?: 1 | 2;
}) {
  return (
    <div className={colSpan === 2 ? 'md:col-span-2' : ''}>
      <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}
